import { Box, Container, CssBaseline, Paper, Typography } from '@mui/material';
import { Link } from '@mui/material';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Logo from 'assets/img/pruff9.svg';
import { getPublicSGRWarrantyRequestFile } from 'lib/api/express/sgrWarrantyRequest';
import AlertBanner from 'lib/designSystem/AlertBanner';
import CustomButton from 'lib/designSystem/Button';
import { rutChanger, validaRut } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ClientConfigState } from 'services/redux/clientConfigSlice';

export function Copyright(props: any) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      style={{ paddingBottom: '10px', marginTop: 32 }}
      {...props}
    >
      {'Powered by'}
      <div style={{ padding: '10px 15px 10px' }}>
        <a href="https://pruff.com/" target="_blank" rel="noreferrer">
          <img width={90} src={Logo} />
        </a>
      </div>
      <Link
        color="inherit"
        href="https://pruff.com/"
        target="_blank"
        rel="noreferrer"
      >
        Pruff
      </Link>{' '}
      {new Date().getFullYear()}
      {'.'}
    </Typography>
  );
}

const SGRValidator = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [certificateFound, setCertificateFound] = useState(false);
  const expressConfig = useSelector(
    (state: { clientConfiguration: ClientConfigState }) =>
      state.clientConfiguration.configs,
  );

  // Form fields
  const [warrantyRequestId, setWarrantyRequestId] = useState('');
  const [clientIdentifier, setClientIdentifier] = useState('');
  const [principalIdentifier, setPrincipalIdentifier] = useState('');

  // Form validation errors
  const [errors, setErrors] = useState({
    warrantyRequestId: '',
    clientIdentifier: '',
    principalIdentifier: '',
  });

  const validateField = (field: string, value: string) => {
    let error = '';

    switch (field) {
      case 'warrantyRequestId':
        if (!value.trim()) {
          error = 'Debe ingresar el número de la garantía';
        } else if (isNaN(Number(value))) {
          error = 'El número debe ser un valor válido';
        }
        break;
      case 'clientIdentifier':
        if (!value.trim()) {
          error = 'Debe ingresar el RUT Afianzado';
        } else if (!validaRut(value)) {
          error = 'RUT Afianzado inválido';
        }
        break;
      case 'principalIdentifier':
        if (!value.trim()) {
          error = 'Debe ingresar el RUT Mandante';
        } else if (!validaRut(value)) {
          error = 'RUT Mandante inválido';
        }
        break;
    }

    return error;
  };

  const handleFieldChange = (field: string, value: string) => {
    // Update field value
    switch (field) {
      case 'warrantyRequestId':
        setWarrantyRequestId(value);
        break;
      case 'clientIdentifier':
        rutChanger(value, setClientIdentifier);
        break;
      case 'principalIdentifier':
        rutChanger(value, setPrincipalIdentifier);
        break;
    }

    // Clear error when user starts typing
    setErrors((prev) => ({
      ...prev,
      [field]: '',
    }));
  };

  const handleFieldBlur = (field: string, value: string) => {
    const error = validateField(field, value);
    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));
  };

  const validation = () => {
    const newErrors = {
      warrantyRequestId: validateField('warrantyRequestId', warrantyRequestId),
      clientIdentifier: validateField('clientIdentifier', clientIdentifier),
      principalIdentifier: validateField(
        'principalIdentifier',
        principalIdentifier,
      ),
    };

    setErrors(newErrors);

    return !Object.values(newErrors).some((error) => error !== '');
  };

  const handleSubmit = async () => {
    if (!validation()) return;

    const domain = window.location.hostname;
    const secretId = domain.split('.')[0];

    setLoading(true);
    setError(null);
    setCertificateFound(false);
    setViewerUrl(null);

    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
    }

    try {
      const pdfData = await getPublicSGRWarrantyRequestFile(
        dispatch,
        secretId,
        parseInt(warrantyRequestId),
        clientIdentifier,
        principalIdentifier,
      );

      if (!pdfData) throw new Error('No se pudo obtener el documento');

      const url = URL.createObjectURL(pdfData.blob);
      const viewerUrl = `${url}#pagemode=none`;

      setViewerUrl(viewerUrl);
      setBlobUrl(url);
      setCertificateFound(true);
    } catch (error: any) {
      console.error('Error fetching certificate:', error);
      setCertificateFound(false);
      if (error.response?.status === 404) {
        setError(
          'No se encontró ningún certificado con los datos proporcionados. Por favor, revise que el número de solicitud, identificación del cliente, identificación del principal y tipo de garantía sean correctos.',
        );
      } else {
        setError(
          'Ocurrió un error inesperado al procesar su solicitud. Por favor, intente nuevamente en unos momentos. Si el problema persiste, contacte al soporte técnico.',
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  return (
    <div
      style={{
        backgroundColor: '#F8FAFC',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Container
        component="main"
        maxWidth="sm"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
        }}
      >
        <CssBaseline />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <div className="div-container">
            <img
              width={200}
              src={`https://pruff-public.s3.amazonaws.com/company-logos/${expressConfig?.companyId}.png`}
              alt="Logo"
            />
          </div>
          <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
            Validador de Certificados SGR
          </Typography>
          <Typography component="p" sx={{ mb: 3, textAlign: 'center' }}>
            Ingrese los datos del certificado de garantía para validar su
            autenticidad
          </Typography>

          <Box sx={{ width: '100%' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Número de la garantía"
                  variant="outlined"
                  id="warrantyRequestId"
                  value={warrantyRequestId}
                  onChange={(e) =>
                    handleFieldChange('warrantyRequestId', e.target.value)
                  }
                  onBlur={(e) =>
                    handleFieldBlur('warrantyRequestId', e.target.value)
                  }
                  placeholder="Ej: 12345"
                  error={!!errors.warrantyRequestId}
                  helperText={errors.warrantyRequestId}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="clientIdentifier"
                  label="RUT Afianzado"
                  placeholder="Ej: 12345678-9"
                  variant="outlined"
                  value={clientIdentifier}
                  onChange={(e) =>
                    handleFieldChange('clientIdentifier', e.target.value)
                  }
                  onBlur={(e) =>
                    handleFieldBlur('clientIdentifier', e.target.value)
                  }
                  error={!!errors.clientIdentifier}
                  helperText={errors.clientIdentifier}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="principalIdentifier"
                  label="RUT Mandante"
                  placeholder="Ej: 98765432-1"
                  variant="outlined"
                  value={principalIdentifier}
                  onChange={(e) =>
                    handleFieldChange('principalIdentifier', e.target.value)
                  }
                  onBlur={(e) =>
                    handleFieldBlur('principalIdentifier', e.target.value)
                  }
                  error={!!errors.principalIdentifier}
                  helperText={errors.principalIdentifier}
                  required
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <CustomButton
                color="primary"
                onClick={handleSubmit}
                fullWidth
                variant="contained"
                disabled={loading}
              >
                {loading ? 'Buscando...' : 'Validar Certificado'}
              </CustomButton>
            </Box>

            {/* Error Alert */}
            {error && !certificateFound && (
              <AlertBanner
                severity="error"
                title="Certificado no encontrado"
                message={error}
              />
            )}

            {/* Certificate Display */}
            {certificateFound && viewerUrl && (
              <Box sx={{ mt: 3, width: '100%' }}>
                <AlertBanner
                  severity="success"
                  title="Certificado encontrado"
                  message="El certificado ha sido validado correctamente."
                />

                <Paper
                  sx={{
                    height: '600px',
                    boxShadow: 'none',
                    border: '1px solid #e0e0e0',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  {loading && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        zIndex: 1,
                      }}
                    >
                      <Typography>Cargando documento...</Typography>
                    </Box>
                  )}

                  <iframe
                    src={viewerUrl}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                    }}
                    title="Certificado de Garantía SGR"
                  />
                </Paper>
              </Box>
            )}
          </Box>
        </Box>
        <Copyright />
      </Container>
    </div>
  );
};

export default SGRValidator;
